import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "../lib/api";
import {
  Globe, Plus, Trash2, Loader2, Send, FileText, Sparkles, CheckCircle2,
  ExternalLink, AlertTriangle, Image as ImageIcon, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { FEATURED_SOURCES } from "./NewsView";
import { GenerationControls } from "./GenerationControls";
import { AdsenseCheck } from "./AdsenseCheck";

const ORIGIN = { ai: "AI photo", feed: "Source image", pexels: "Pexels" };

const inputCls =
  "w-full bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/60 transition-colors";

const emptySite = {
  name: "", api_url: "", username: "", app_password: "",
  default_category: null, default_author: null, default_status: "draft", seo_plugin: "yoast",
  article_style: "gradient", gradient_colors: ["#E91E63", "#B44BCB", "#9C27B0"],
  internal_links: true, submit_for_indexing: true,
};

/* The article as it will look on the site: the same inline styles the
   publish step applies (gradient headings, spaced paragraphs), with the
   inline images in their slots. Links and uploads happen at publish. */
const gradientCss = (c = emptySite.gradient_colors) =>
  `background: linear-gradient(135deg, ${c[0]} 0%, ${c[1]} 50%, ${c[2]} 100%); -webkit-background-clip: text; ` +
  "-webkit-text-fill-color: transparent; background-clip: text; font-weight: bold;";
const previewHtml = (a, site) => {
  let html = (a.content_html || "").replace(/<p>\s*\[IMAGE_PLACEHOLDER_(\d+)\]\s*<\/p>|\[IMAGE_PLACEHOLDER_(\d+)\]/g, (_m, x, y) => {
    const src = (a.inline_image_urls || {})[x || y];
    return src ? `<div style="margin:1.5em 0"><img src="${src}" alt="" style="width:100%;height:auto;display:block;border-radius:6px"/></div>` : "";
  });
  // Model-written HTML: no scripts or inline handlers in the preview.
  html = html.replace(/<script[\s\S]*?<\/script>/gi, "").replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  if ((site?.article_style || "gradient") === "plain") return html;
  const g = gradientCss(site?.gradient_colors?.length === 3 ? site.gradient_colors : undefined);
  return html.replace(/<h([234])>/g, `<h$1 style="${g}">`).replace(/<p>/g, '<p style="margin-bottom: 1em; line-height: 1.6;">');
};

const SEO_PLUGINS = [["yoast", "Yoast SEO"], ["rankmath", "Rank Math"], ["none", "No SEO plugin"]];

export const BlogView = ({ seedUrl }) => {
  const [sites, setSites] = useState([]);
  const [articles, setArticles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const [siteForm, setSiteForm] = useState(null);
  const [savingSite, setSavingSite] = useState(false);

  const [url, setUrl] = useState(seedUrl || "");
  const [topic, setTopic] = useState("");
  const [angle, setAngle] = useState("");
  const [brandId, setBrandId] = useState("");
  const [words, setWords] = useState(1200);
  const [inlineSource, setInlineSource] = useState("ai_generated");
  const [inlineCount, setInlineCount] = useState(2);
  const [preview, setPreview] = useState(null);
  const [imageSource, setImageSource] = useState("ai_generated");
  const [imageRatio, setImageRatio] = useState("16:9");
  const [fixing, setFixing] = useState("");
  const [cleaning, setCleaning] = useState(false);
  const [writing, setWriting] = useState(false);

  const [publishing, setPublishing] = useState(null);
  const [taxonomy, setTaxonomy] = useState({ categories: [], authors: [] });

  const load = async () => {
    try {
      const [s, a, b] = await Promise.all([
        api.get("/wp-sites"), api.get("/articles"), api.get("/brands"),
      ]);
      setSites(s.data); setArticles(a.data); setBrands(b.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (seedUrl) setUrl(seedUrl); }, [seedUrl]);

  // Articles render their featured image in the background after generation.
  useEffect(() => {
    if (!articles.some((a) => a.status === "writing" || a.image_status === "pending" || a.inline_status === "pending")) return undefined;
    const t = setInterval(() => api.get("/articles").then(({ data }) => setArticles(data)).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [articles]);

  const saveSite = async () => {
    if (!siteForm.name.trim() || !siteForm.api_url.trim()) return toast.error("Name and site URL are required");
    setSavingSite(true);
    try {
      if (siteForm.id) await api.put(`/wp-sites/${siteForm.id}`, siteForm);
      else await api.post("/wp-sites", siteForm);
      toast.success("Connected to WordPress");
      setSiteForm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect", { duration: 12000 });
    } finally { setSavingSite(false); }
  };

  const write = async (ignoreAdsense = false) => {
    if (!url.trim() && !topic.trim()) return toast.error("Give a source link or a topic");
    setWriting(true);
    try {
      await api.post("/generate/article", {
        url: url.trim(), topic: topic.trim(), angle: angle.trim(),
        brand_id: brandId || null, words: Number(words), with_image: imageSource !== "none",
        featured_image_source: imageSource, featured_ratio: imageRatio,
        inline_images_source: inlineSource, inline_images_count: Number(inlineCount),
        ignore_adsense: ignoreAdsense,
      });
      toast.success("Writing the article - it appears below as it's ready (a few minutes with images)");
      setUrl(""); setTopic(""); setAngle("");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not write that article";
      if (e?.response?.status === 422 && String(detail).startsWith("Skipped by AdSense-safe")) {
        toast.warning(detail, { duration: 20000, action: { label: "Write anyway", onClick: () => write(true) } });
      } else toast.error(detail, { duration: 14000 });
    } finally { setWriting(false); }
  };

  const openPublish = async (article) => {
    if (sites.length === 0) return toast.error("Connect a WordPress site first");
    const site = sites[0];
    setPublishing({ article, site_id: site.id, status: site.default_status || "draft",
                    category: site.default_category, author: site.default_author });
    try {
      const { data } = await api.get(`/wp-sites/${site.id}/taxonomy`);
      setTaxonomy(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the site's categories");
    }
  };

  const doPublish = async () => {
    const { article, ...body } = publishing;
    setPublishing((p) => ({ ...p, busy: true }));
    try {
      const { data } = await api.post(`/articles/${article.id}/publish`, body);
      toast.success(
        body.status === "publish" ? "Published to WordPress" : "Sent to WordPress as a draft",
        { duration: 10000, description: data.seo?.saved === false ? undefined : data.seo?.note,
          action: data.link ? { label: "Open", onClick: () => window.open(data.link, "_blank") } : undefined }
      );
      if (data.seo?.saved === false) toast.warning(data.seo.note, { duration: 16000 });
      const extras = [
        data.inline_images?.placed ? `${data.inline_images.placed} inline image${data.inline_images.placed === 1 ? "" : "s"}` : "",
        data.internal_links?.length ? `${data.internal_links.length} internal link${data.internal_links.length === 1 ? "" : "s"}` : "",
        data.indexing?.indexing_status === "submitted" ? "sent for indexing" : "",
      ].filter(Boolean);
      if (extras.length) toast(extras.join(" · "), { duration: 8000 });
      if (data.featured_image?.ok === false) {
        toast.warning(`Published without the featured image: ${data.featured_image.error}`, { duration: 20000 });
      }
      setPublishing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publishing failed", { duration: 14000 });
      setPublishing((p) => ({ ...p, busy: false }));
    }
  };

  const redoImage = async (id, source) => {
    try {
      await api.post(`/articles/${id}/image`, source ? { featured_image_source: source } : {});
      setArticles((list) => list.map((x) => (x.id === id ? { ...x, image_status: "pending", image_error: null } : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not restart the image"); }
  };

  const attachFeatured = async (id) => {
    setFixing(id);
    try {
      await api.post(`/articles/${id}/featured`);
      toast.success("Featured image added to the WordPress post");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add the featured image", { duration: 16000 });
    } finally { setFixing(""); }
  };

  // Strip the writer's leaked planning notes from articles already live.
  const cleanPublished = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/articles/clean-published");
      if (data.fixed.length) toast.success(`Cleaned ${data.fixed.length} of ${data.checked} published article${data.checked === 1 ? "" : "s"}`,
        { description: data.fixed.map((f) => f.title).slice(0, 4).join(" · "), duration: 12000 });
      else toast(`Checked ${data.checked} published article${data.checked === 1 ? "" : "s"} - nothing to clean`);
      if (data.needs_attention.length) toast.warning(`${data.needs_attention.length} still read like notes - edit them in WordPress`,
        { description: data.needs_attention.map((f) => f.title).slice(0, 4).join(" · "), duration: 20000 });
      if (data.failed.length) toast.error(`${data.failed.length} could not be checked: ${data.failed[0].error}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Clean-up failed"); } finally { setCleaning(false); }
  };

  const removeArticle = async (id) => { await api.delete(`/articles/${id}`); toast("Draft deleted"); load(); };

  return (
    <div className="space-y-8" data-testid="blog-view">
      <div>
        <p className="text-xs font-semibold tracking-wider uppercase text-indigo-400 mb-2">Long form</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-50">Blog</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">
          Write an article from a source story or a topic, then send it to WordPress with its
          featured image and SEO fields. Drafts go over as drafts until you change the status.
        </p>
      </div>

      <GenerationControls />

      {/* Sites */}
      <div className="glass rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-sky-300" />
            <h3 className="font-heading font-bold text-slate-100">WordPress sites</h3>
          </div>
          <button onClick={() => setSiteForm({ ...emptySite })}
            className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200">
            <Plus className="w-3.5 h-3.5" /> Connect a site
          </button>
        </div>
        {sites.length === 0 ? (
          <p className="text-xs text-slate-500">
            None yet. You will need an Application Password from WordPress
            (Users → Profile → Application Passwords) — not your login password.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {sites.map((s) => (
              <div key={s.id} className="rounded-xl border border-slate-800 p-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-100 truncate">{s.name}</p>
                  <p className="text-[11px] text-slate-500 truncate">{s.api_url}</p>
                  {s.connection?.ok && (
                    <p className="flex items-center gap-1 text-[11px] text-emerald-400 mt-1">
                      <CheckCircle2 className="w-3 h-3" /> {s.connection.user}
                      {s.connection.can_publish ? "" : " · cannot publish"}
                    </p>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setSiteForm({ ...s })} className="text-[11px] text-slate-500 hover:text-slate-300">Edit</button>
                  <button onClick={async () => { await api.delete(`/wp-sites/${s.id}`); load(); }}
                    className="text-slate-600 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AdsenseCheck sites={sites} />

      {/* Write */}
      <div className="glass rounded-2xl p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-300" />
          <h3 className="font-heading font-bold text-slate-100">Write an article</h3>
        </div>
        <input data-testid="article-url" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="Source article URL (optional)" className={inputCls} />
        <input data-testid="article-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
          placeholder="…or a topic to write about" className={inputCls} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input value={angle} onChange={(e) => setAngle(e.target.value)} placeholder="Angle (optional)" className={inputCls} />
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)} className={inputCls}>
            <option value="">No brand</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 shrink-0">Words</span>
            <input type="number" min={300} max={2500} step={100} value={words}
              onChange={(e) => setWords(e.target.value)} className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <label className="block">
            <span className="text-xs text-slate-400">Images inside</span>
            <select value={inlineSource} onChange={(e) => setInlineSource(e.target.value)} className={`${inputCls} mt-1`}>
              <option value="ai_generated">AI generated</option>
              <option value="none">None</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-400">How many</span>
            <input type="number" min={0} max={20} value={inlineCount} disabled={inlineSource === "none"}
              onChange={(e) => setInlineCount(e.target.value)} className={`${inputCls} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs text-slate-400">Featured image</span>
            <select value={imageSource} onChange={(e) => setImageSource(e.target.value)} className={`${inputCls} mt-1`}>
              {FEATURED_SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-400">Aspect ratio</span>
            <select value={imageRatio} onChange={(e) => setImageRatio(e.target.value)} disabled={imageSource === "none"} className={`${inputCls} mt-1`}>
              <option value="16:9">16:9 (Landscape)</option>
              <option value="1:1">1:1 (Square)</option>
            </select>
          </label>
        </div>
        <button data-testid="write-article" onClick={() => write()} disabled={writing}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold transition-colors disabled:opacity-60">
          {writing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
          {writing ? "Writing…" : "Write the article"}
        </button>
      </div>

      {/* Drafts */}
      {articles.some((a) => a.wordpress?.id) && (
        <div className="flex justify-end -mb-4">
          <button onClick={cleanPublished} disabled={cleaning}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-700 text-xs font-semibold text-slate-300 hover:border-slate-600 disabled:opacity-50">
            {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Clean published articles
          </button>
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>
      ) : articles.length === 0 ? (
        <div className="glass rounded-2xl py-16 flex flex-col items-center gap-3">
          <FileText className="w-8 h-8 text-slate-700" />
          <p className="text-slate-500 text-sm">No articles yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {articles.map((a, i) => (
            <motion.div key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.3) }}
              className="glass rounded-2xl overflow-hidden flex" data-testid="article-row">
              <div className="w-28 shrink-0 bg-slate-900 flex items-center justify-center">
                {a.image_url ? <img src={a.image_url} alt="" className="w-full h-full object-cover" />
                  : a.image_status === "pending" ? <Loader2 className="w-5 h-5 animate-spin text-indigo-300" />
                  : <ImageIcon className="w-5 h-5 text-slate-700" />}
              </div>
              <div className="flex-1 min-w-0 p-4 space-y-1.5">
                <p className="font-semibold text-slate-100 text-sm">{a.title}</p>
                {a.status === "writing" && (
                  <p className="flex items-center gap-1.5 text-xs text-indigo-300">
                    <Loader2 className="w-3 h-3 animate-spin" /> Writing the article - title, SEO fields, body, then images
                  </p>
                )}
                {a.status === "failed" && <p className="text-xs text-rose-300">{a.error || "Writing failed"} - delete it and try again.</p>}
                {a.inline_status === "pending" && (
                  <p className="flex items-center gap-1.5 text-[11px] text-slate-400">
                    <Loader2 className="w-3 h-3 animate-spin" /> Making the images inside the article
                    {a.inline_images ? ` (${Object.keys(a.inline_images).length} done)` : ""}
                  </p>
                )}
                {a.meta_description && <p className="text-xs text-slate-500 line-clamp-2">{a.meta_description}</p>}
                <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-600">
                  {a.focus_keyphrase && <span className="text-indigo-300" title={`SEO title: ${a.seo_title || ""}`}>{a.focus_keyphrase}</span>}
                  {a.wordpress?.seo?.saved === true && <span className="text-emerald-400">SEO fields saved</span>}
                  {a.wordpress?.id && !(a.wordpress.featured_image ? a.wordpress.featured_image.ok : a.wordpress.featured_media) && a.image_status !== "none" && (
                    <span className="flex items-center gap-1.5 text-amber-400" title={a.wordpress?.featured_image?.error || ""}>
                      <AlertTriangle className="w-3 h-3" /> No featured image on the post
                      <button onClick={() => attachFeatured(a.id)} disabled={fixing === a.id}
                        className="ml-1 px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 font-semibold">
                        {fixing === a.id ? "Adding…" : "Add it now"}
                      </button>
                    </span>
                  )}
                  {a.wordpress?.seo?.saved === false && (
                    <span className="flex items-center gap-1 text-amber-400" title={a.wordpress.seo.note}>
                      <AlertTriangle className="w-3 h-3" /> SEO fields not saved
                    </span>
                  )}
                  {a.source?.site && <span>from {a.source.site}</span>}
                  {a.image_status === "ready" && ORIGIN[a.image_origin] && (
                    <span title={a.image_prompt || ""}>
                      {ORIGIN[a.image_origin]}
                      {a.image_credit?.photographer ? ` · ${a.image_credit.photographer}` : ""}
                    </span>
                  )}
                  {a.image_status === "failed" && (
                    <span className="flex items-center gap-1 text-amber-400" title={a.image_error || ""}>
                      <AlertTriangle className="w-3 h-3" /> image failed
                    </span>
                  )}
                  {["ready", "partial"].includes(a.inline_status) && (
                    <span>{Object.keys(a.inline_images || {}).length} inline image{Object.keys(a.inline_images || {}).length === 1 ? "" : "s"}</span>
                  )}
                  {a.wordpress?.indexing?.indexing_status === "submitted" && <span className="text-emerald-400">Sent for indexing</span>}
                  {!a.wordpress?.link && a.status !== "writing" && a.status !== "failed" && a.image_status !== "pending" && (
                    <button onClick={() => redoImage(a.id, a.image_status === "none" ? "ai_generated" : "")}
                      className="flex items-center gap-1 text-slate-500 hover:text-indigo-300">
                      <RefreshCw className="w-3 h-3" /> {a.image_status === "none" ? "Add image" : "New image"}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  {a.wordpress?.link ? (
                    <a href={a.wordpress.link} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 text-xs font-semibold">
                      <ExternalLink className="w-3 h-3" /> On {a.wordpress.site_name}
                    </a>
                  ) : a.status === "writing" || a.status === "failed" ? null : (
                    <button onClick={() => openPublish(a)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25 text-xs font-semibold transition-colors">
                      <Send className="w-3 h-3" /> Send to WordPress
                    </button>
                  )}
                  {a.status !== "writing" && a.status !== "failed" && (
                    <button onClick={() => api.get(`/articles/${a.id}`).then(({ data }) => setPreview(data))}
                      className="px-3 py-1.5 rounded-lg border border-slate-700 text-slate-300 hover:border-slate-600 text-xs font-semibold">
                      Preview
                    </button>
                  )}
                  <button onClick={() => removeArticle(a.id)} className="p-1.5 text-slate-600 hover:text-rose-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-white text-slate-900 border-slate-200 max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogTitle className="sr-only">Article preview</DialogTitle>
          {preview && (
            <article className="space-y-4">
              <h1 className="text-3xl font-extrabold leading-tight text-center">{preview.title}</h1>
              {preview.image_url && <img src={preview.image_url} alt="" className="w-full rounded-xl" />}
              <div className="text-[15px] text-slate-700 [&_h2]:text-2xl [&_h2]:mt-6 [&_h2]:mb-2 [&_h3]:text-xl [&_h3]:mt-4 [&_h3]:mb-2 [&_blockquote]:border-l-4 [&_blockquote]:border-pink-400 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4 [&_ul]:list-disc [&_ul]:pl-6"
                dangerouslySetInnerHTML={{ __html: previewHtml(preview, sites[0]) }} />
              <p className="text-[11px] text-slate-400 border-t pt-3">
                SEO title: {preview.seo_title} · Keyphrase: {preview.focus_keyphrase} · Internal links are added when it is published.
              </p>
            </article>
          )}
        </DialogContent>
      </Dialog>

      {/* Site dialog */}
      <Dialog open={!!siteForm} onOpenChange={(o) => !o && setSiteForm(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle className="font-heading">WordPress site</DialogTitle></DialogHeader>
          {siteForm && (
            <div className="space-y-3">
              <input value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                placeholder="Name (e.g. Enpulsed)" className={inputCls} />
              <input value={siteForm.api_url} onChange={(e) => setSiteForm({ ...siteForm, api_url: e.target.value })}
                placeholder="https://yourblog.com" className={inputCls} />
              <input value={siteForm.username} onChange={(e) => setSiteForm({ ...siteForm, username: e.target.value })}
                placeholder="WordPress username" className={inputCls} />
              <input type="password" value={siteForm.app_password}
                onChange={(e) => setSiteForm({ ...siteForm, app_password: e.target.value })}
                placeholder="Application Password" className={inputCls} />
              <p className="text-[11px] text-slate-600">
                In WordPress: Users → Profile → Application Passwords. Spaces are fine.
                The connection is tested on save.
              </p>
              <div className="flex gap-2">
                {["draft", "publish"].map((v) => (
                  <button key={v} onClick={() => setSiteForm({ ...siteForm, default_status: v })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                      siteForm.default_status === v ? "bg-indigo-500/15 border-indigo-500/50 text-indigo-200"
                        : "border-slate-700 text-slate-500"}`}>
                    Default: {v}
                  </button>
                ))}
              </div>
              <label className="block">
                <span className="text-xs text-slate-400">SEO plugin</span>
                <select value={siteForm.seo_plugin || "yoast"} onChange={(e) => setSiteForm({ ...siteForm, seo_plugin: e.target.value })}
                  className={`${inputCls} mt-1`}>
                  {SEO_PLUGINS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <span className="block text-[11px] text-slate-600 mt-1">
                  Focus keyphrase, SEO title and meta description are filled in on every article.
                </span>
              </label>
              <div>
                <span className="text-xs text-slate-400">Article style</span>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {[["gradient", "Gradient headings (Enpulsed)"], ["plain", "Theme default"]].map(([v, l]) => (
                    <button key={v} onClick={() => setSiteForm({ ...siteForm, article_style: v })}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                        (siteForm.article_style || "gradient") === v ? "bg-indigo-500/15 border-indigo-500/50 text-indigo-200"
                          : "border-slate-700 text-slate-500"}`}>{l}</button>
                  ))}
                  {(siteForm.article_style || "gradient") === "gradient" && [0, 1, 2].map((i) => (
                    <input key={i} type="color" value={(siteForm.gradient_colors || emptySite.gradient_colors)[i]}
                      onChange={(e) => {
                        const c = [...(siteForm.gradient_colors || emptySite.gradient_colors)];
                        c[i] = e.target.value;
                        setSiteForm({ ...siteForm, gradient_colors: c });
                      }} className="w-8 h-8 rounded border border-slate-700 bg-transparent cursor-pointer" />
                  ))}
                </div>
                {(siteForm.article_style || "gradient") === "gradient" && (
                  <p className="mt-2 text-lg font-extrabold" style={{ background: `linear-gradient(135deg, ${(siteForm.gradient_colors || emptySite.gradient_colors).join(", ")})`,
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>So What Actually Happened Here</p>
                )}
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={siteForm.internal_links !== false}
                  onChange={(e) => setSiteForm({ ...siteForm, internal_links: e.target.checked })} className="accent-indigo-500" />
                <span className="text-xs text-slate-300">Link 2-3 of this site's own related posts in each article</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={siteForm.submit_for_indexing !== false}
                  onChange={(e) => setSiteForm({ ...siteForm, submit_for_indexing: e.target.checked })} className="accent-indigo-500" />
                <span className="text-xs text-slate-300">Submit published posts to RocketIndexer (needs the API key on the server)</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setSiteForm(null)} className="px-4 py-2 rounded-lg text-sm text-slate-400">Cancel</button>
            <button onClick={saveSite} disabled={savingSite}
              className="px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold flex items-center gap-2">
              {savingSite && <Loader2 className="w-4 h-4 animate-spin" />} Test &amp; save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish dialog */}
      <Dialog open={!!publishing} onOpenChange={(o) => !o && setPublishing(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle className="font-heading">Send to WordPress</DialogTitle></DialogHeader>
          {publishing && (
            <div className="space-y-3">
              <p className="text-sm text-slate-300">{publishing.article.title}</p>
              <select value={publishing.site_id} onChange={(e) => setPublishing({ ...publishing, site_id: e.target.value })} className={inputCls}>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select value={publishing.category || ""} onChange={(e) => setPublishing({ ...publishing, category: Number(e.target.value) || null })} className={inputCls}>
                <option value="">No category</option>
                {taxonomy.categories.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.count})</option>)}
              </select>
              <select value={publishing.author || ""} onChange={(e) => setPublishing({ ...publishing, author: Number(e.target.value) || null })} className={inputCls}>
                <option value="">Default author</option>
                {taxonomy.authors.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <div className="flex gap-2">
                {[["draft", "Save as draft"], ["publish", "Publish live"]].map(([v, l]) => (
                  <button key={v} onClick={() => setPublishing({ ...publishing, status: v })}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border ${
                      publishing.status === v ? "bg-indigo-500/15 border-indigo-500/50 text-indigo-200"
                        : "border-slate-700 text-slate-500"}`}>{l}</button>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setPublishing(null)} className="px-4 py-2 rounded-lg text-sm text-slate-400">Cancel</button>
            <button onClick={doPublish} disabled={publishing?.busy}
              className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold flex items-center gap-2">
              {publishing?.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
